import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { DelegationData } from '../api';
import { Contact, isPerson, isRoom, Room } from '../model';
import { getSecondaryUser, selectRooms } from '../store/helpers';
import { actions } from '../store/utils';
import { ReactComponent as ArrowLeft } from './buttons/arrow-left.svg';
import css from './CalendarDelegationPicker.module.scss';
import ContactPhoto from './ContactPhoto';
import { performContactSearch } from './ParticipantInput';

type DelegateItem = {
   item: Contact;
   type: "Room" | "Person";
}

export function CalendarDelegationPicker({ delegations }: { delegations: DelegationData }) {
   const dispatch = useDispatch()

   const [value, setValue] = useState("")
   const [filteredItems, setFilteredItems] = useState<DelegateItem[]>([])

   const allRooms = useSelector(selectRooms)
   const myself = useSelector(getSecondaryUser)

   const [isOpen, setIsOpen] = useState(false)

   useEffect(() => {
      if (!isOpen) {
         setValue("")
         setFilteredItems([])
      }
   }, [isOpen])

   const getItems = useCallback(async (name: string): Promise<DelegateItem[]> => {
      if (myself === undefined ) return []
      const people = await getPeopleItems(name, myself, delegations, dispatch)
      const rooms = await getRoomItems(delegations, allRooms)
      return [...people, ...rooms].filter(c => !name || c.item.name.toLowerCase().includes(name.toLowerCase()) || c.item.emailAddress.toLowerCase().includes(name.toLowerCase()))
   }, [myself, delegations, dispatch, allRooms])

   useEffect(() => {
      if (value.trim()) {
         getItems(value).then(setFilteredItems)
      } else {
         setFilteredItems([])
      }
   }, [value, getItems])

   const handleSelect = (item: DelegateItem) => {
      setIsOpen(false)
      dispatch(actions.delegatedLogin({ delegatee: item.item.emailAddress, type: item.type }))
   }

   return <div className={classNames(css.delegationPicker, isOpen && css.isOpen)}>
      <button onClick={() => setIsOpen(!isOpen)}>
         {myself && renderPersonItem({item: myself, type: "Person"}, false)}
         <ArrowLeft className={css.cheveron}/>
      </button>
      {isOpen && <div className={css.searchContainer}>
         <input 
            className={css.input}
            placeholder="Type to search..." 
            autoFocus 
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={() => setTimeout(() => setIsOpen(false), 200)}
         />
         {filteredItems.length > 0 && <div className={css.menu}>
            {filteredItems.map((item, idx) => (
               <div 
                  key={`${item.item.emailAddress}-${idx}`}
                  className={css.item}
                  onClick={() => handleSelect(item)}
                  role="button"
                  tabIndex={0}
               >
                  {renderPersonItem(item, false)}
               </div>
            ))}
         </div>}
      </div>}
   </div>
}

async function getPeopleItems(name: string, myself: Contact, delegations: DelegationData, dispatch: Dispatch<any>): Promise<DelegateItem[]> {
   if (!delegations.people) return []

   const isDelegated = myself.emailAddress !== delegations.delegator.emailAddress
   
   let base: Contact[]
   if (delegations.people.all) {
      const searchResults = await performContactSearch(name, dispatch)
      if (!isDelegated) {
         base = searchResults.filter(c => c.emailAddress !== delegations.delegator.emailAddress)
      } else {
         base = searchResults.filter(c => c.emailAddress !== myself.emailAddress)
      }
   } else {
      base = isDelegated 
         ? [delegations.delegator, ...delegations.people.people].filter(c => c.emailAddress !== myself.emailAddress)
         : delegations.people.people
   }

   return base.map(r => ({item: r, type: "Person"}))
}

async function getRoomItems(delegations: DelegationData, allRooms: Room[]): Promise<DelegateItem[]> {
   if (!delegations.room) return []

   return (delegations.room.all ? allRooms : allRooms.filter(r => delegations.room.rooms.includes(r.emailAddress)))
      .map(r => ({item: r, type: "Room"}))
}

function renderPersonItem(item: DelegateItem, isHighlighted: boolean) {
   return <div className={classNames(css.item, isHighlighted && css.highlighted)} key={item.item.emailAddress}>
      <ContactPhoto contact={item.item} />
      <div className={css.details}>
         <div className={css.name}>{item.item.name}</div>
         <div className={css.desc}>{getDesc(item.item)}</div>
      </div>
   </div>;
}

function getDesc(item: Contact) {
   if (isPerson(item)) {
      return [item.jobTitle, item.department].filter(x => x).join(', ')
   }
   if (isRoom(item)) {
      switch (item.roomType) {
         case 'MeetingRoom': return "Meeting Room"
         case 'ParkingSpace': return "Carppark"
         case 'HotDesk': return "Hot Desk"
         case 'Locker': return "Locker"
      }
   }
   return ""
}

function getItemValue({item}: DelegateItem) {
   return item.emailAddress;
}
